import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { searchVehicles } from '../api';
import type { EventChoice } from './EventModal';

interface Props {
  onChoose: (choice: EventChoice, vehicle: { id: number; registration_number: string } | null) => void;
  onClose: () => void;
}

// Første trin når man registrerer noget fra /expenses: hvad er det for en slags?
//
// Findes fordi en værksted-regning hører hjemme som en begivenhed, ikke som en løs udgift —
// historikken er det der fortæller hvornår tingene sidst er lavet. Uden det her trin kunne man
// taste en værksted-udgift ind uden at den nogensinde dukkede op i bilens historik.
//
// Alt undtagen "Anden udgift" kræver en bil: rfo_maintenance.vehicle_id er NOT NULL, og en
// begivenhed uden bil giver heller ikke mening.
export default function RegisterChooserModal({ onChoose, onClose }: Props) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<EventChoice>('maintenance_unplanned');
  const [vehicle, setVehicle] = useState<{ id: number; registration_number: string } | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<{ id: number; registration_number: string }[]>([]);

  const needsVehicle = choice !== 'expense';

  useEffect(() => {
    if (!search) return;
    const timer = setTimeout(async () => setResults(await searchVehicles(search)), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Udledt frem for at nulstille i effekten — ellers rydder et tomt søgefelt listen
  // via en ekstra render i stedet for bare at lade være med at vise noget.
  const visibleResults = search ? results : [];

  const OPTIONS: { key: EventChoice; label: string }[] = [
    { key: 'maintenance_unplanned', label: t('events.unplanned') },
    { key: 'maintenance_planned',   label: t('events.planned') },
    { key: 'inspection',            label: t('events.inspection') },
    { key: 'checkup',               label: t('events.checkup') },
    { key: 'expense',               label: t('expense.title') },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-md">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="modal-title text-base sm:text-lg">{t('events.choose_type')}</h2>
          <button onClick={onClose} className="modal-close p-2 -mr-2">&times;</button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="label">{t('expense.category_label')}</label>
            <select className="input" value={choice} onChange={e => setChoice(e.target.value as EventChoice)}>
              {OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>

          {needsVehicle && (
            <div>
              <label className="label">{t('fleet.col_vehicle')}</label>
              {vehicle ? (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-ink-900">{vehicle.registration_number}</span>
                  <button type="button" onClick={() => { setVehicle(null); setSearch(''); }}
                    className="text-xs text-ink-300 hover:text-rose-700">&times;</button>
                </div>
              ) : (
                <>
                  <input className="input" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={t('fleet.col_vehicle')} autoFocus />
                  {visibleResults.length > 0 && (
                    <ul className="mt-1 border border-ink-100 rounded-lg divide-y divide-ink-100 max-h-40 overflow-y-auto">
                      {visibleResults.map(r => (
                        <li key={r.id}>
                          <button type="button" onClick={() => { setVehicle(r); setResults([]); setSearch(''); }}
                            className="w-full text-left px-3 py-2 font-mono text-sm hover:bg-cream-100">
                            {r.registration_number}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-4 sm:px-6 pb-6 pt-2 gap-3">
          <button onClick={onClose} className="btn btn-secondary">{t('modal.cancel')}</button>
          <button
            onClick={() => onChoose(choice, vehicle)}
            disabled={needsVehicle && !vehicle}
            className="btn btn-primary">
            {t('modal.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
